/**
 * Carrier Blocker — the shapes /callcenter/admin/carrier-blocks returns.
 *
 * The rule rejects an inbound call by its ORIGINATING CARRIER, which is the only signal
 * available on the FIRST call: every other detector needs accumulated history, so a fresh
 * number gets two or three free calls before anything can act on it.
 */

export type CarrierTier = 'wholesale' | 'attack'
export type CarrierMode = 'light' | 'attack'

/**
 * What a rule may be scoped to.
 *
 * NOT the same vocabulary as phones.line_type_detail, which the server maps onto these:
 * `voip` covers both fixedVoip and nonFixedVoip, and a caller whose line type is unknown is
 * matched only by `any`. The mapping lives server-side in LineType; the UI only offers the
 * four values a rule can hold.
 */
export type CarrierLineType = 'any' | 'landline' | 'mobile' | 'voip'

export const CARRIER_LINE_TYPES: CarrierLineType[] = ['any', 'landline', 'mobile', 'voip']

export interface ICarrierRule {
    id: number
    carrier_name: string
    tier: CarrierTier
    line_type: CarrierLineType
    active: boolean
    note: string | null
    created_by: number | null
    created_at: string
    updated_at: string
    /**
     * Whether this rule is enforced RIGHT NOW, which is not the same as `active`: an active
     * rule on the attack tier does nothing while the blocker runs in light mode. That
     * distinction is the most confusing thing about this feature and the server computes it
     * so the screen cannot describe it differently from the code.
     */
    enforced_now?: boolean
}

export interface ICarrierRulesResponse {
    rules: {
        data: ICarrierRule[]
        current_page: number
        last_page: number
        total: number
    }
    mode: CarrierMode
    active: boolean
    dry_run: boolean
}

export interface ICarrierPreset {
    label: string
    help: string
    values: { [key: string]: number | string }
}

export interface ICarrierSettingsResponse {
    values: {
        carrier_block_active: boolean
        carrier_block_dry_run: boolean
        carrier_block_mode: CarrierMode
        carrier_block_cache_ttl: number
    }
    presets: { [name: string]: ICarrierPreset }
    schema: { [key: string]: any }
    rule_counts: { [tier: string]: { total: number, active: number } }
    enforced_tiers: CarrierTier[]
    effective_state: {
        state: 'off' | 'dry_run' | 'live' | 'attack'
        label: string
        detail: string
    }
    /**
     * Attack mode is temporary BY DESIGN and nothing else reminds anybody to leave it — this
     * is what the screen shows instead of a reminder.
     */
    mode_since: { since: string | null, hours: number | null }
    history: Array<{
        id: number
        user_id: number | null
        user?: { email?: string }
        changes: { [k: string]: { from: any, to: any } }
        comment: string
        created_at: string
    }>
}

export interface ICarrierCandidate {
    carrier_name: string
    reached: number
    junk: number
    leads: number
    junk_pct: number
    lead_pct: number
    /**
     * The carrier's biggest line-type slice, and the full breakdown.
     *
     * Shown at the point a rule gets written, because two carriers turned out to separate
     * sharply here and in the direction nobody assumes: Google and Twilio SMS run their junk
     * on `mobile` while their `nonFixedVoip` slice is the clean one. Values are raw
     * phones.line_type_detail (mobile, fixedVoip, nonFixedVoip, …, or `unknown`), not the
     * four-value rule vocabulary.
     */
    line_type_dominant: string | null
    line_type_slices: { [lineTypeDetail: string]: number }
}

export interface ICarrierCandidatesResponse {
    candidates: ICarrierCandidate[]
    window: { days: number, min_reached: number }
    junk_reason_ids: number[]
}

/**
 * What the rule COSTS, and why there are two booking numbers rather than one.
 *
 * A replay over 2026-07-27..08-04 audited all 23 `New Customer - Booked` tags the light list
 * would have blocked: 20 were under a minute with no lead attached, eleven of them one to two
 * seconds. Exactly 3 were real. So `booking_tags_raw` is not a rougher version of the truth —
 * it is off by roughly 8x, and an alarm wired to it fires on mis-tagged spam daily.
 * `qualified_bookings` is the number to act on.
 */
export interface ICarrierCost {
    available: boolean
    reason?: string
    flagged_calls?: number
    qualified_bookings?: number
    direct_leads?: number
    booking_tags_raw?: number
    qualifies_when?: string
    note?: string
}

export interface ICarrierStatsResponse {
    per_day: Array<{
        day: string
        wholesale_rejected: number
        wholesale_would_block: number
        attack_rejected: number
        attack_would_block: number
    }>
    totals: {
        wholesale: { rejected: number, would_block: number }
        attack: { rejected: number, would_block: number }
    }
    cost: ICarrierCost
    window: { days: number, since: string }
}
